import { twMerge } from 'utilities/src/twMerge';
import React, { useMemo } from 'react';
import { drop, pipe, maxBy } from 'remeda';
import ButtonLink from '../../ButtonLink';
import PurifiedText from '../../PurifiedText';
import { NewMenuItem } from '../../../data/MenuItem';

import ExtraContent from './ExtraContent';

type MegaMenuProps = {
  subMenuItem: NewMenuItem;
};

function MegaMenu({ subMenuItem }: MegaMenuProps) {
  const { sections } = subMenuItem;

  const TOTAL_COLUMNS = 4;
  const shouldSplitSections = sections.length < TOTAL_COLUMNS;

  const sectionToSplit = useMemo(() => {
    if (!shouldSplitSections) return null;

    const biggest = pipe(
      sections,
      drop(1),
      maxBy((section) => section.links.length),
    );

    return biggest ?? null;
  }, [sections, shouldSplitSections]);

  const splitSectionInto = TOTAL_COLUMNS - sections.length + 1;

  return (
    <div
      className={twMerge(
        'absolute left-0 top-0 z-40 mt-20 flex w-full bg-white drop-shadow-lg',
        'pointer-events-none invisible -translate-y-10 transform opacity-0 duration-200',
        'group-hover:pointer-events-auto group-hover:visible group-hover:translate-y-0 group-hover:opacity-100',
      )}
    >
      <div className={twMerge('flex w-full')}>
        {sections.map((section, index) => {
          const isFirstSection = index === 0;
          const isSecondSection = index === 1;

          return (
            <div
              key={section.id}
              className={twMerge(
                'mdL:px-6 lgS:px-8 py-8 lg:px-16',
                {
                  'bg-site-neutrals-25 lgS:pl-8 lgS:pr-16 lg:pl-36 2xl:pl-[calc((100vw-110rem)/2)]':
                    isFirstSection,
                  'lgS:pl-16': isSecondSection,
                },
                splitSectionInto === 3 && section.id === sectionToSplit?.id && 'mdL:w-full lg:w-3/5 2xl:w-2/5',
                splitSectionInto === 2 && section.id === sectionToSplit?.id && 'mdL:pr-0',
              )}
            >
              <div
                className={twMerge(
                  'lgS:mx-auto flex h-full flex-col',
                  isFirstSection && 'lgS:w-[15.625rem] mdL:w-[14rem]',
                )}
              >
                <span
                  className={twMerge(
                    {
                      'text-site-neutrals-900 site-display-xs after:text-site-primary-600 font-medium after:content-["."]':
                        isFirstSection,
                    },
                    {
                      'text-site-neutrals-400 site-overline-m before:bg-site-primary-600 font-bold before:mb-[1px] before:mr-2 before:inline-block before:h-2 before:w-2 before:rounded-sm before:content-[""]':
                        !isFirstSection && !!section.title,
                    },
                  )}
                >
                  {section.title}
                </span>

                {section.description && (
                  <PurifiedText className="text-site-neutrals-600 site-paragraph-m mt-2 [&_a]:underline">
                    {section.description}
                  </PurifiedText>
                )}

                <div
                  className={twMerge(
                    'gap-y-3',
                    section.title && 'mt-4',
                    section.id === sectionToSplit?.id
                      ? 'mdL:gap-x-6 lgS:gap-x-16 grid lg:gap-x-32'
                      : 'flex flex-col',
                    splitSectionInto === 3 && 'grid-cols-3',
                    splitSectionInto === 2 && 'grid-cols-2',
                  )}
                >
                  {section.links.map((link) => (
                    <a
                      key={link.id}
                      href={link.link_url}
                      className="text-site-neutrals-600 w-fit font-medium transition duration-300 hover:underline hover:brightness-75"
                    >
                      {link.link_label}
                    </a>
                  ))}
                </div>

                {isFirstSection && subMenuItem.extraContent && (
                  <ExtraContent extraContent={subMenuItem.extraContent} />
                )}

                {isSecondSection && subMenuItem.cta && (
                  <ButtonLink
                    useNextLink={false}
                    href={subMenuItem.cta.link_url}
                    variant="Tertiary"
                    variantColor="Black"
                    size="Medium"
                    classes="border-none mt-12 text-left !justify-start"
                    iconName="ArrowForward"
                    iconSize="IconS"
                  >
                    {subMenuItem.cta.link_label}
                  </ButtonLink>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default MegaMenu;
